import { translationKey } from '../i18n';
import { localize, readAppLanguage } from '../i18n';
import { copyText, shareText } from '../utils/shareText';
import useNavigate from '../hooks/useSafeNavigate';
import SingleLineText from '../components/ui/InternationalText';
import Button from '../components/ui/FitButton';
import FamilyAccountNotice from '../components/FamilyAccountNotice';
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Typography, TextField, IconButton, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { getDeviceUuid, createInviteCode, joinByInviteCode, getMyGuardians } from '../api';
import { getAppInstallText, INVITE_URL } from '../utils/storeLinks';
import AppToast from '../components/AppToast';
import { clearAllScreenCaches } from '../utils/localCache';

const TOP_PADDING = '8px';

export default function FamilyCareSetupPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialRole = searchParams.get('role');
    const directRole = initialRole === 'ward' || initialRole === 'guardian';
    const [step, setStep] = useState(() => {
        if (initialRole === 'ward') return 'ward-code';
        if (initialRole === 'guardian') return 'guardian-join';
        return 'select';
    }); // 'select' | 'guardian-join' | 'ward-code'
    const [direction, setDirection] = useState('forward');
    const autoRoleTriggered = useRef(false);

    // Ward (대상자): 코드 생성 state
    const [inviteCode, setInviteCode] = useState('');
    const [codeLoading, setCodeLoading] = useState(initialRole === 'ward');
    const [wardLinked, setWardLinked] = useState(false);
    const wardPollRef = useRef(null);
    const wardInitialCountRef = useRef(null);

    // Guardian (보호자): 코드 입력 state
    const [joinCode, setJoinCode] = useState('');
    const [joining, setJoining] = useState(false);
    const [joinSuccess, setJoinSuccess] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const handleBack = () => {
        if (step === 'select' || directRole) {
            navigate(-1);
        } else {
            setDirection('back');
            setStep('select');
            if (wardPollRef.current) { clearInterval(wardPollRef.current); wardPollRef.current = null; }
        }
    };

    // Cleanup polling on unmount
    useEffect(() => {
        return () => { if (wardPollRef.current) clearInterval(wardPollRef.current); };
    }, []);

    // ?role=ward/guardian → 역할별 흐름 바로 시작
    useEffect(() => {
        if (searchParams.get('role') === 'ward' && !autoRoleTriggered.current) {
            autoRoleTriggered.current = true;
            handleWardSelect();
        } else if (searchParams.get('role') === 'guardian' && !autoRoleTriggered.current) {
            autoRoleTriggered.current = true;
        }
    }, [searchParams]);

    // 보호자: 코드 입력 화면으로
    const handleGuardianSelect = () => {
        setDirection('forward');
        setStep('guardian-join');
    };

    // 보호자: 코드 입력 → 연결 → 가족 돌봄 페이지로 이동
    const handleGuardianJoin = async () => {
        if (joinCode.length < 6 || joining) return;
        setJoining(true);
        try {
            const uuid = getDeviceUuid();
            await joinByInviteCode(uuid, joinCode);
            clearAllScreenCaches({ identityChanged: true });
            setJoinSuccess(true);
            setTimeout(() => navigate('/guardian', { replace: true }), 1500);
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || localize("연결 실패. 코드를 확인해주세요."), severity: 'error' });
        } finally {
            setJoining(false);
        }
    };

    // 대상자: 코드 생성 + 폴링 시작
    const handleWardSelect = async () => {
        setCodeLoading(true);
        try {
            const uuid = getDeviceUuid();
            const guardians = await getMyGuardians(uuid).catch(() => []);
            wardInitialCountRef.current = guardians.length;
            const res = await createInviteCode(uuid, 'GUARDIAN');
            setInviteCode(res.inviteCode);
            try { void navigator.clipboard?.writeText(res.inviteCode)?.catch(() => {}); } catch {}
            setDirection('forward');
            setStep('ward-code');

            // 보호자가 코드 입력하면 자동 감지
            console.log('[ward-poll] started, initial count:', wardInitialCountRef.current);
            wardPollRef.current = setInterval(async () => {
                try {
                    const g = await getMyGuardians(uuid, true);
                    console.log('[ward-poll] guardians:', g.length, 'initial:', wardInitialCountRef.current);
                    if (g.length > wardInitialCountRef.current) {
                        clearInterval(wardPollRef.current);
                        wardPollRef.current = null;
                        setWardLinked(true);
                        setTimeout(() => navigate('/my-guardians', { replace: true }), 2000);
                    }
                } catch (err) {
                    console.error('[ward-poll] error:', err);
                }
            }, 3000);
        } catch (err) {
            setSnackbar({ open: true, message: localize("연결 코드 생성에 실패했어요"), severity: 'error' });
        } finally {
            setCodeLoading(false);
        }
    };

    const handleCopyCode = async () => {
        try {
            await copyText(inviteCode);
            setSnackbar({ open: true, message: localize("코드가 복사되었어요"), severity: 'success' });
        } catch {
            setSnackbar({ open: true, message: localize("복사하지 못했어요. 화면의 코드를 직접 전달해주세요"), severity: 'error' });
        }
    };

    const handleRequestCode = async () => {
        const message = localize("[집요한약알림] 가족 약 챙기기\n\n가족이 내 약 복용을 확인할 수 있도록 연결해주세요.\n\n아래 링크를 눌러 코드를 만들어주세요:\n{v0}\n\n앱이 없다면 아래 링크로 설치해주세요.\n{v1}", { v0: INVITE_URL, v1: getAppInstallText() });
        try {
            if (await shareText(message) === 'copied') {
                setSnackbar({ open: true, message: localize("공유 내용이 복사되었어요"), severity: 'success' });
            }
        } catch {
            setSnackbar({ open: true, message: localize("공유하지 못했어요. 연결 코드를 직접 전달해주세요"), severity: 'error' });
        }
    };

    const handleShare = async () => {
        const message = localize("[집요한약알림] 가족 약 챙기기 초대\n\n연결 코드: {v0}\n\n앱에서 가족 약 챙기기 → 내가 가족 약을 챙길게요 → 위 코드를 입력해주세요.\n(5분 내 입력)\n\n앱이 없다면 아래 링크로 설치해주세요.\n{v1}\n\n초대 페이지: {v2}", { v0: inviteCode, v1: getAppInstallText(), v2: INVITE_URL });
        try {
            if (await shareText(message) === 'copied') {
                setSnackbar({ open: true, message: localize("공유 내용이 복사되었어요"), severity: 'success' });
            }
        } catch {
            setSnackbar({ open: true, message: localize("공유하지 못했어요. 연결 코드를 직접 전달해주세요"), severity: 'error' });
        }
    };

    const renderSelect = () => (
        <Box sx={{
            animation: direction === 'forward' ? 'fcSlideRight 0.25s ease-out' : 'fcSlideLeft 0.25s ease-out',
        }}>
            <SingleLineText sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#1a1a1a', lineHeight: 1.3, mb: 0.5 }}>{localize("가족 약 챙기기")}</SingleLineText>
            <SingleLineText sx={{ fontSize: '1.05rem', color: '#777', mb: 3 }}>{localize("어떤 역할인가요?")}</SingleLineText>

            <FamilyAccountNotice />

            {/* 보호자 카드 → 코드 입력 */}
            <Box
                onClick={handleGuardianSelect}
                role="button"
                tabIndex={0}
                sx={{
                    p: 2.5, borderRadius: 4, mb: 1.5, cursor: 'pointer',
                    border: '2px solid #f0f0f0', bgcolor: '#fff',
                    '&:active': { border: '2px solid #ffe566', bgcolor: '#fffdf0' },
                    transition: 'all 0.15s ease',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 48, height: 48, borderRadius: '50%', bgcolor: '#fffbeb',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <VolunteerActivismIcon sx={{ fontSize: 24, color: '#8a7000' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <SingleLineText sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#1a1a1a' }}>{localize("내가 가족 약을 챙길게요")}</SingleLineText>
                        <SingleLineText sx={{ fontSize: '1rem', color: '#888', mt: 0.3 }}>{localize("가족이 보내준 코드를 입력해요")}</SingleLineText>
                    </Box>
                </Box>
            </Box>

            {/* 대상자 카드 → 코드 생성 */}
            <Box
                onClick={codeLoading ? undefined : handleWardSelect}
                {...(!codeLoading ? { role: 'button', tabIndex: 0 } : {})}
                sx={{
                    p: 2.5, borderRadius: 4, cursor: 'pointer',
                    border: '2px solid #f0f0f0', bgcolor: '#fff',
                    '&:active': { border: '2px solid #ffe566', bgcolor: '#fffdf0' },
                    transition: 'all 0.15s ease',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 48, height: 48, borderRadius: '50%', bgcolor: '#f0fdf4',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <FavoriteBorderIcon sx={{ fontSize: 24, color: '#15803d' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <SingleLineText sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#1a1a1a' }}>{localize("가족이 내 약을 챙겨줘요")}</SingleLineText>
                        <SingleLineText sx={{ fontSize: '1rem', color: '#888', mt: 0.3 }}>{localize("보호자에게 보낼 코드를 만들어요")}</SingleLineText>
                    </Box>
                </Box>
            </Box>
        </Box>
    );

    // 보호자: 코드 입력 화면
    const renderGuardianJoin = () => (
        <Box sx={{
            animation: direction === 'forward' ? 'fcSlideRight 0.25s ease-out' : 'fcSlideLeft 0.25s ease-out',
        }}>
            {joinSuccess ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Box sx={{
                        animation: 'fcBounce 0.5s ease-out',
                        '@keyframes fcBounce': {
                            '0%': { transform: 'scale(0)', opacity: 0 },
                            '60%': { transform: 'scale(1.2)' },
                            '100%': { transform: 'scale(1)', opacity: 1 },
                        },
                    }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 72, color: '#22c55e' }} />
                    </Box>
                    <SingleLineText sx={{ fontSize: '1.4rem', fontWeight: 800, color: '#1a1a1a', mt: 2 }}>{localize("가족이 연결되었어요!")}</SingleLineText>
                    <SingleLineText sx={{ fontSize: '1.05rem', color: '#777', mt: 0.5 }}>{localize("가족 돌봄 페이지로 이동합니다")}</SingleLineText>
                </Box>
            ) : (
                <>
                    <SingleLineText sx={{ fontSize: '1.3rem', fontWeight: 800, color: '#1a1a1a', mb: 0.5, textAlign: 'center' }}>{localize("가족이 보내준 코드를 입력하세요")}</SingleLineText>
                    <SingleLineText sx={{ fontSize: '1rem', color: '#777', mb: 3, textAlign: 'center', lineHeight: 1.6 }}>{localize("가족이 만든 6자리 코드를 입력해주세요")}</SingleLineText>

                    <Box sx={{ bgcolor: '#f9fafb', borderRadius: 4, py: 3, px: 2.5, mb: 1.5, textAlign: 'center' }}>
                        <TextField
                            fullWidth
                            placeholder="000000"
                            value={joinCode}
                            onChange={(e) => setJoinCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                            inputProps={{ maxLength: 6, inputMode: 'numeric', style: { fontSize: '2.4rem', fontWeight: 800, textAlign: 'center', letterSpacing: '0.3em' } }}
                            variant="standard"
                            sx={{ '& .MuiInput-root': { '&::before': { borderBottom: 'none' }, '&::after': { borderBottom: 'none' }, '&:hover:not(.Mui-disabled)::before': { borderBottom: 'none' } } }}
                        />
                    </Box>

                    <SingleLineText sx={{ fontSize: '0.95rem', color: '#999', textAlign: 'center', mb: 3 }}>{localize("5분 내에 입력해야 해요")}</SingleLineText>

                    <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                        <Button
                            variant="outlined" fullWidth
                            startIcon={<SendIcon />}
                            onClick={handleRequestCode}
                            sx={{
                                borderColor: '#e5e7eb', color: '#333', borderRadius: 3,
                                py: 1.5, fontWeight: 700, fontSize: '1.05rem',
                                ...(readAppLanguage() !== 'ko' && { px: 1 }),
                            }}
                        >{localize("코드 요청")}</Button>
                        <Button
                            variant="contained" fullWidth
                            disabled={joinCode.length < 6 || joining}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={handleGuardianJoin}
                            sx={{
                                bgcolor: joinCode.length >= 6 ? '#ffe566' : '#e5e7eb',
                                color: joinCode.length >= 6 ? '#5a4a00' : '#888',
                                borderRadius: 3, py: 1.5, fontWeight: 700, fontSize: '1.05rem',
                                '&:hover': { bgcolor: '#e8d040' },
                                '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#888' },
                            }}
                        >
                            {joining ? <CircularProgress size={24} sx={{ color: '#5a4a00' }} /> : localize("연결하기")}
                        </Button>
                    </Box>

                    {/* 코드 발급 가이드 */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f9fafb', borderRadius: 3 }}>
                        <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#555', mb: 1.5 }}>{localize("코드는 이렇게 받을 수 있어요")}</SingleLineText>
                        {[
                            { num: '1', text: translationKey('가족의 폰에서 집요한약알림 앱 열기') },
                            { num: '2', text: translationKey('설정 → 가족 약 챙기기') },
                            { num: '3', text: translationKey('"내 약을 가족이 챙겨줄 거예요" 선택') },
                            { num: '4', text: translationKey('화면에 뜨는 6자리 코드를 여기에 입력') },
                        ].map((item) => (
                            <Box key={item.num} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2, mb: 1 }}>
                                <Box sx={{
                                    width: 22, height: 22, borderRadius: '50%', bgcolor: '#ffe566',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.1,
                                }}>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#5a4a00' }}>{item.num}</Typography>
                                </Box>
                                <Typography sx={{ fontSize: '0.92rem', color: '#666', lineHeight: 1.5, ...(readAppLanguage() !== 'ko' && { flex: 1, minWidth: 0, overflowWrap: 'anywhere' }) }}>{localize(item.text)}</Typography>
                            </Box>
                        ))}
                        <Typography sx={{ fontSize: '0.85rem', color: '#aaa', mt: 1.5, lineHeight: 1.5 }}>{localize("가족에게 직접 말하기 어려우면")}{'\n'}{localize("\"코드 요청\" 버튼으로 메시지를 보내보세요.")}</Typography>
                    </Box>
                </>
            )}
        </Box>
    );

    // 대상자: 코드 생성 화면
    const renderWardCode = () => (
        <Box sx={{
            animation: direction === 'forward' ? 'fcSlideRight 0.25s ease-out' : 'fcSlideLeft 0.25s ease-out',
        }}>
            {wardLinked ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Box sx={{
                        animation: 'fcBounce 0.5s ease-out',
                        '@keyframes fcBounce': {
                            '0%': { transform: 'scale(0)', opacity: 0 },
                            '60%': { transform: 'scale(1.2)' },
                            '100%': { transform: 'scale(1)', opacity: 1 },
                        },
                    }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 72, color: '#22c55e' }} />
                    </Box>
                    <SingleLineText sx={{ fontSize: '1.4rem', fontWeight: 800, color: '#1a1a1a', mt: 2 }}>{localize("보호자가 연결되었어요!")}</SingleLineText>
                    <SingleLineText sx={{ fontSize: '1.05rem', color: '#777', mt: 0.5 }}>{localize("이제 보호자가 약 복용을 확인할 수 있어요")}</SingleLineText>
                </Box>
            ) : (
                <>
                    <SingleLineText sx={{ fontSize: '1.3rem', fontWeight: 800, color: '#1a1a1a', mb: 0.5, textAlign: 'center' }}>{localize("보호자에게 코드를 보내주세요")}</SingleLineText>
                    <Typography sx={{ fontSize: '1rem', color: '#777', mb: 3, textAlign: 'center', lineHeight: 1.6 }}>{localize("보호자가 앱에서 이 코드를 입력하면")}{'\n'}{localize("내 복용 현황을 확인할 수 있어요")}</Typography>

                    <Box sx={{ bgcolor: '#f9fafb', borderRadius: 4, py: 3, mb: 1.5, textAlign: 'center', minHeight: 104, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {codeLoading || !inviteCode ? (
                            <CircularProgress size={28} sx={{ color: '#8a7000' }} />
                        ) : (
                            <Typography sx={{ fontSize: '2.4rem', fontWeight: 800, color: '#1a1a1a', letterSpacing: '0.3em' }}>
                                {inviteCode}
                            </Typography>
                        )}
                    </Box>

                    <SingleLineText sx={{ fontSize: '0.95rem', color: '#999', textAlign: 'center', mb: 3 }}>{localize("5분 내에 입력해야 해요")}</SingleLineText>

                    <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                        <Button
                            variant="outlined" fullWidth
                            startIcon={<ContentCopyIcon />}
                            onClick={handleCopyCode}
                            disabled={codeLoading || !inviteCode}
                            sx={{
                                borderColor: '#e5e7eb', color: '#333', borderRadius: 3,
                                py: 1.5, fontWeight: 700, fontSize: '1.05rem',
                            }}
                        >{localize("복사")}</Button>
                        <Button
                            variant="contained" fullWidth
                            startIcon={<ShareIcon />}
                            onClick={handleShare}
                            disabled={codeLoading || !inviteCode}
                            sx={{
                                bgcolor: '#ffe566', color: '#5a4a00', borderRadius: 3,
                                py: 1.5, fontWeight: 700, fontSize: '1.05rem',
                                '&:hover': { bgcolor: '#e8d040' },
                                '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#888' },
                            }}
                        >{localize("공유하기")}</Button>
                    </Box>

                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <CircularProgress size={16} sx={{ color: '#ccc', mr: 1, verticalAlign: 'middle' }} />
                        <Typography component="span" sx={{ fontSize: '0.95rem', color: '#aaa' }}>{localize("보호자가 코드를 입력하면 자동으로 연결돼요")}</Typography>
                    </Box>

                    {/* 보호자 입력 가이드 */}
                    <Box sx={{ p: 2, bgcolor: '#f9fafb', borderRadius: 3 }}>
                        <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#555', mb: 1.5 }}>{localize("보호자는 이렇게 입력해요")}</SingleLineText>
                        {[
                            { num: '1', text: translationKey('보호자의 폰에서 집요한약알림 앱 열기') },
                            { num: '2', text: translationKey('설정 → 가족 약 챙기기') },
                            { num: '3', text: translationKey('"내가 가족 약을 챙길게요" 선택') },
                            { num: '4', text: translationKey('위 6자리 코드 입력 → 연결 완료') },
                        ].map((item) => (
                            <Box key={item.num} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2, mb: 1 }}>
                                <Box sx={{
                                    width: 22, height: 22, borderRadius: '50%', bgcolor: '#ffe566',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.1,
                                }}>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#5a4a00' }}>{item.num}</Typography>
                                </Box>
                                <Typography sx={{ fontSize: '0.92rem', color: '#666', lineHeight: 1.5, ...(readAppLanguage() !== 'ko' && { flex: 1, minWidth: 0, overflowWrap: 'anywhere' }) }}>{localize(item.text)}</Typography>
                            </Box>
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );

    return (
        <Box sx={{
            minHeight: '100vh', bgcolor: '#fff',
            '@keyframes fcSlideRight': {
                from: { transform: 'translateX(24px)', opacity: 0 },
                to: { transform: 'translateX(0)', opacity: 1 },
            },
            '@keyframes fcSlideLeft': {
                from: { transform: 'translateX(-24px)', opacity: 0 },
                to: { transform: 'translateX(0)', opacity: 1 },
            },
        }}>
            {/* Header */}
            <Box sx={{ pt: TOP_PADDING, px: 1.5, pb: 1.5 }}>
                <IconButton
                    aria-label={localize("뒤로가기")}
                    onClick={handleBack}
                    sx={{ color: '#333', width: 48, height: 48, p: 0, ml: -0.5 }}
                >
                    <ArrowBackIcon />
                </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ px: 3 }}>
                {step === 'select' && renderSelect()}
                {step === 'guardian-join' && renderGuardianJoin()}
                {step === 'ward-code' && renderWardCode()}
            </Box>

            <AppToast
                open={snackbar.open}
                autoHideDuration={snackbar.severity === 'error' ? 4000 : 2000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                severity={snackbar.severity}
                message={localize(snackbar.message)}
            />
        </Box>
    );
}
